import { useState } from 'react';
import { Icon } from '../../primitives';
import type { RecentMatch, RecentPhase } from './useRecentMatches';
import { undoablePhases } from './useRecentMatches';

const phaseText: Record<RecentPhase, string> = {
  saving: 'Saving in the store',
  saved: 'Saved in the store',
  failed: 'Not saved',
  undoing: 'Undoing in the store',
  undone: 'Undone',
  undo_failed: 'Not undone',
};

/**
 * What was matched in this sitting, newest first, each with the store's answer
 * and an Undo that asks the store to put back what the match replaced
 * (CO-170). It is a status region: a screen reader hears "Saving" become
 * "Saved" without having to look for it.
 */
export function RecentMatches({
  items,
  busy,
  onUndo,
}: {
  items: RecentMatch[];
  /** An undo request is on its way. */
  busy: boolean;
  onUndo: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  if (items.length === 0) return null;
  const latest = items[0]!;
  const tone = latest.phase === 'failed' || latest.phase === 'undo_failed' ? 'warning' : latest.phase === 'saved' || latest.phase === 'undone' ? 'success' : 'progress';
  const canUndo = (item: RecentMatch) => undoablePhases.includes(item.phase) && !busy;
  return (
    <section className={`match-recent match-recent--${tone}`} aria-label="Recent matches" data-testid="match-recent">
      <div className="match-recent__latest">
        <p role="status" className="match-recent__text" data-testid="match-recent-status">
          <Icon name={tone === 'success' ? 'check-circle' : tone === 'warning' ? 'alert-triangle' : 'refresh'} size={16} className="match-recent__icon" />
          <span>
            <strong>{phaseText[latest.phase]}.</strong> <span className="match-recent__what">{latest.productLabel} → {latest.articleLabel}</span>
            {latest.message ? <span className="match-recent__why"> {latest.message}</span> : null}
          </span>
        </p>
        <div className="match-recent__actions">
          <button
            type="button"
            className="button button--secondary button--small"
            disabled={!canUndo(latest)}
            onClick={() => onUndo(latest.key)}
            aria-keyshortcuts="U"
            data-testid="match-undo"
          >
            Undo <kbd className="kbd" aria-hidden="true">U</kbd>
          </button>
          {items.length > 1 ? (
            <button type="button" className="button button--ghost button--small" aria-expanded={open} aria-controls="match-recent-list" onClick={() => setOpen(!open)}>
              {open ? 'Hide earlier' : `Earlier (${items.length - 1})`}
            </button>
          ) : null}
        </div>
      </div>
      {open ? (
        <ul id="match-recent-list" className="match-recent__list">
          {items.slice(1).map((item) => (
            <li key={item.key}>
              <span className="match-recent__what" title={`${item.productLabel} → ${item.articleLabel}`}>
                {item.productLabel} → {item.articleLabel}
              </span>
              <span className="match-recent__phase">{phaseText[item.phase]}</span>
              <button type="button" className="button button--secondary button--small" disabled={!canUndo(item)} onClick={() => onUndo(item.key)}>
                Undo
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
